import {useMutation, useQueryClient} from "@tanstack/react-query";
import {
  bancApi,
  interventionAreaApi,
  technicianApi,
  userApi,
} from "../services/technicianService";
import {authApi} from "../services/AuthService";
import {useDispatch} from "react-redux";
import {useNavigate} from "react-router-dom";
import {Logout} from "../store/actions/AuthActions";
import {showToastModal} from "../jsx/components/shared/ToastModalSwal";
import {useTranslation} from "react-i18next";

const getBankAccountErrorKey = (error, fallbackKey) => {
  const code = Number(error?.response?.data?.code);
  const message = String(
    error?.response?.data?.error || error?.response?.data?.message || "",
  ).toLowerCase();

  if (
    code === 12001 ||
    /selected bank account.*cannot be deleted/.test(message) ||
    /account bank can not be deleted/.test(message)
  ) {
    return "bankAccountCannotDeleteSelected";
  }
  if (code === 12002 || /invalid iban/.test(message)) {
    return "bankAccountInvalidIban";
  }
  if (code === 12003 || /invalid bic/.test(message)) {
    return "bankAccountInvalidBic";
  }
  return fallbackKey;
};

export const useUpdateTechnician = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {t} = useTranslation();
  const {mutate: update, isPending: isLoadingUpdate} = useMutation({
    mutationFn: technicianApi.update,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["getMe"]});
      showToastModal({
        success: true,
        title: "Succès",
        message: "La mise à jour du compte a été effectuée avec succès.",
      });
    },
    onError: () => {
      showToastModal({
        success: false,
        title: "Erreur",
        message: "Une erreur est survenue, veuillez réessayer !",
      });
    },
  });

  const {mutate: updateUser} = useMutation({
    mutationFn: userApi.update,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["getMe"]});
      showToastModal({
        success: true,
        title: "Succès",
        message: "La mise à jour du compte a été effectuée avec succès.",
      });
    },
    onError: () => {
      showToastModal({
        success: false,
        title: "Erreur",
        message: "Une erreur est survenue, veuillez réessayer !",
      });
    },
  });
  const {mutateAsync: updateSettings} = useMutation({
    mutationFn: userApi.update,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["getMe"]});
      showToastModal({
        success: true,
        title: "Succès",
        message: "La mise à jour du compte a été effectuée avec succès.",
      });
    },
    onError: () => {
      showToastModal({
        success: false,
        title: "Erreur",
        message: "Une erreur est survenue, veuillez réessayer !",
      });
    },
  });

  const {mutate: contactSupport, isPending: isLoadingContact} = useMutation({
    mutationFn: userApi.contactSupport,
  });

  const {mutate: blockUser} = useMutation({
    mutationFn: userApi.block,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["getMe"]});
      queryClient.invalidateQueries({queryKey: ["offers"]});
      queryClient.invalidateQueries({queryKey: ["chatRooms"]});
      queryClient.invalidateQueries({queryKey: ["blocked"]});

      showToastModal({
        success: true,
        title: "Blocage de l’utilisateur",
        message: "Utilisateur bloqué avec succès.",
      });
    },
    onError: () => {
      showToastModal({
        success: false,
        title: "Erreur",
        message: "Une erreur est survenue, veuillez réessayer !",
      });
    },
  });
  const {mutate: unBlockUser} = useMutation({
    mutationFn: userApi.unBlock,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["blocked"]});
      queryClient.invalidateQueries({queryKey: ["getMe"]});
      queryClient.invalidateQueries({queryKey: ["offers"]});
      queryClient.invalidateQueries({queryKey: ["chatRooms"]});
      showToastModal({
        success: true,
        title: "Déblocage de l’utilisateur",
        message: "Utilisateur débloqué avec succès.",
      });
    },
    onError: () => {
      showToastModal({
        success: false,
        title: "Erreur",
        message: "Une erreur est survenue, veuillez réessayer !",
      });
    },
  });
  const {mutate: updateFCMUser} = useMutation({
    mutationFn: authApi.addFcmToken,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["getMe"]});
    },
  });

  const {mutate: addBanc} = useMutation({
    mutationFn: bancApi.add,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["getMe"]});
      queryClient.invalidateQueries({queryKey: ["stripeConnectStatus"]});
    },
    onError: (error) => {
      showToastModal({
        success: false,
        title: t("bankAccountErrorTitle"),
        message: t(getBankAccountErrorKey(error, "bankAccountAddFailed")),
      });
    },
  });
  const {mutate: verifyEmail} = useMutation({
    mutationFn: authApi.verifyEmail,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["getMe"]});
      showToastModal({
        success: true,
        title: "E-mail envoyé",
        message:
          "Un e-mail de vérification a été envoyé. Consultez votre boîte mail.",
      });
    },
    onError: () => {
      showToastModal({
        success: false,
        title: "Erreur",
        message: "Une erreur est survenue, veuillez réessayer !",
      });
    },
  });

  const {mutate: removeAccount} = useMutation({
    mutationFn: authApi.removeAccount,
    onSuccess: (result) => {
      return showToastModal({
        success: true,
        title: "Suppression de compte",
        message:
          result?.message ||
          "Votre compte est désactivé et récupérable pendant 30 jours.",
        thenCallback: () => dispatch(Logout(navigate)),
      });
    },
    onError: (error) => {
      showToastModal({
        success: false,
        title: "Suppression impossible",
        message:
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Une erreur est survenue, veuillez réessayer !",
      });
    },
  });
  const {mutate: updateBanc} = useMutation({
    mutationFn: bancApi.update,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["getMe"]});
      queryClient.invalidateQueries({queryKey: ["stripeConnectStatus"]});
      showToastModal({
        success: true,
        title: "Compte bancaire modifié",
        message: "Votre compte bancaire a été modifié avec succès.",
      });
    },
    onError: (error) => {
      showToastModal({
        success: false,
        title: t("bankAccountErrorTitle"),
        message: t(getBankAccountErrorKey(error, "bankAccountUpdateFailed")),
      });
    },
  });

  const {mutate: deleteBanc} = useMutation({
    mutationFn: bancApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["getMe"]});
      queryClient.invalidateQueries({queryKey: ["stripeConnectStatus"]});
      showToastModal({
        success: true,
        title: "Suppression du compte bancaire",
        message: "Le compte bancaire a été supprimé avec succès.",
      });
    },
    onError: (error) => {
      showToastModal({
        success: false,
        title: t("bankAccountErrorTitle"),
        message: t(getBankAccountErrorKey(error, "bankAccountDeleteFailed")),
      });
    },
  });
  const {mutate: addIntervention} = useMutation({
    mutationFn: interventionAreaApi.add,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["getMe"]});
      showToastModal({
        success: true,
        title: "Modification du profil",
        message: "Votre profil a été modifié avec succès.",
      });
    },
    onError: () => {
      showToastModal({
        success: false,
        title: "Erreur",
        message: "Une erreur est survenue, veuillez réessayer !",
      });
    },
  });
  const {mutate: addSubCategories} = useMutation({
    mutationFn: technicianApi.updateSubCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["getMe"]});
      showToastModal({
        success: true,
        title: "Modification du profil",
        message: "Votre profil a été modifié avec succès.",
      });
    },
    onError: () => {
      showToastModal({
        success: false,
        title: "Erreur",
        message: "Une erreur est survenue, veuillez réessayer !",
      });
    },
  });
  const {mutate: deleteIntervention} = useMutation({
    mutationFn: interventionAreaApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["getMe"]});
      showToastModal({
        success: true,
        title: "Modification du profil",
        message: "Votre profil a été modifié avec succès.",
      });
    },
    onError: () => {
      showToastModal({
        success: false,
        title: "Erreur",
        message: "Une erreur est survenue, veuillez réessayer !",
      });
    },
  });
  const {mutate: updatePassword} = useMutation({
    mutationFn: authApi.updatePassword,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["getMe"]});
      return showToastModal({
        success: true,
        title: "Mot de passe",
        message: "Votre mot de passe a été changé avec succès.",
        thenCallback: () => dispatch(Logout(navigate)),
      });
    },
    onError: (error) => {
      if (error?.response?.data?.error === "Wrong credentials")
        showToastModal({
          success: false,
          title: "Erreur",
          message: "L’ancien mot de passe est incorrect.",
        });
      else
        showToastModal({
          success: false,
          title: "Erreur",
          message: error.message,
        });
    },
  });

  const onUpdatePassword = ({newPassword, oldPassword}) => {
    updatePassword({
      body: {
        newPassword,
        oldPassword,
      },
    });
  };

  const onContactSupport = ({subject, content}, resetForm) => {
    contactSupport(
      {subject, content},
      {
        onSuccess: () => {
          resetForm();
          showToastModal({
            success: true,
            title: "Envoi de message",
            message: "Votre message est envoyé avec succès.",
          });
        },
        onError: () => {
          resetForm();
          showToastModal({
            success: false,
            message: "Erreur lors de l'envoi de votre message.",
            title: "Ooopss, erreur d'envoie",
          });
        },
      },
    );
  };
  const onAddSubCatgeory = (subCategories) => {
    addSubCategories({body: {subCategorieIds: subCategories}});
  };
  const onDeleteZone = (interventionAreaIds) => {
    deleteIntervention({interventionAreaIds});
  };

  const onRemoveAccount = () => {
    removeAccount();
  };
  const onVerifyEmail = ({email}) => {
    verifyEmail({email});
  };

  const onAddZone = (interventionAreaList) => {
    addIntervention({interventionAreaList});
  };

  const onDeleteBanc = (id) => {
    deleteBanc(id);
  };

  const onAddBanc = (banc, options) => {
    addBanc({body: banc}, options);
  };
  const onUpdateBanc = ({body, id}, options) => {
    updateBanc({body, id}, options);
  };
  const onUpdateTech = ({id, body}) => {
    update({id, body});
  };

  const onUpdateUser = ({id, body}) => {
    updateUser({id, body});
  };
  const onUpdateSettings = ({id, body}) => {
    return updateSettings({id, body});
  };

  const onBlockUser = ({blockedId}) => {
    blockUser({blockedId});
  };

  const onUnBlockUser = ({blockedId}) => {
    unBlockUser({blockedId});
  };
  const onUpdateFcmUser = ({id, body}) => {
    updateFCMUser({id, body});
  };
  return {
    onUpdateTech,
    onAddZone,
    onDeleteZone,
    onAddSubCatgeory,
    onUpdateSettings,
    onAddBanc,
    onUpdateBanc,
    onDeleteBanc,
    onUpdatePassword,
    onVerifyEmail,
    onUpdateUser,
    onUpdateFcmUser,
    onBlockUser,
    onUnBlockUser,
    onRemoveAccount,
    onContactSupport,
    isLoadingContact,
    isLoadingUpdate,
  };
};
